/*
 * Japanese Craft Finder — quiz engine (vanilla JS, no framework)
 *
 * Responsibilities:
 *  - Render the 4-question diagnostic UI into each `.craft-finder[data-cf-root]` container.
 *  - Classify answers Q1-Q3 into a 3-digit type key (000-111) and resolve one of 8 craft types.
 *  - Keep all 8 spirits highlighted through the quiz so the resulting type stays a surprise
 *    (revealing it mid-quiz would weaken the "find out your type" email incentive).
 *  - Persist answers to localStorage, accept hand-off via URL params, and emit GA4 events.
 *
 * Q4 is an interest tag only and never affects the type key.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'craft_finder_state_v1';

  // --- 設問定義（EN/JA原文ママ。bit は 0/1、Q4 は tag のみ） --------------
  var QUESTIONS = [
    {
      id: 'q1',
      en: 'Who are you hoping to find something for?',
      answers: [
        { id: 'A', en: 'For my everyday life', bit: '0', icon: '🏠' },
        { id: 'B', en: 'For my home or collection', bit: '0', icon: '🗄️' },
        { id: 'C', en: 'As a thoughtful gift for someone special', bit: '1', icon: '🎁' },
        { id: 'D', en: 'For a celebration or important milestone', bit: '1', icon: '🎊' }
      ]
    },
    {
      id: 'q2',
      en: 'Which sounds more like you?',
      answers: [
        { id: 'A', en: 'I love beautiful things I can use every day', bit: '0', icon: '☕' },
        { id: 'B', en: 'I enjoy turning everyday moments into small rituals', bit: '0', icon: '🍵' },
        { id: 'C', en: 'I like objects that give a room character', bit: '1', icon: '🏺' },
        { id: 'D', en: 'I collect pieces worth keeping for years', bit: '1', icon: '🔑' }
      ]
    },
    {
      id: 'q3',
      en: 'What draws you in first?',
      answers: [
        { id: 'A', en: 'The shape, color, and texture', bit: '0', icon: '🎨' },
        { id: 'B', en: 'How beautifully it fits into modern life', bit: '0', icon: '✨' },
        { id: 'C', en: "The maker's hands and creative process", bit: '1', icon: '🤲' },
        { id: 'D', en: 'The history and tradition behind it', bit: '1', icon: '📜' }
      ]
    },
    {
      id: 'q4',
      en: 'Where would you like to begin your Japanese craft journey?',
      helper: 'This won’t change your type — it just tailors what we show you.',
      answers: [
        { id: 'A', en: 'Table & Tea', tag: 'TABLE_TEA', icon: '🍵' },
        { id: 'B', en: 'Home & Atmosphere', tag: 'HOME_ATMOSPHERE', icon: '🏡' },
        { id: 'C', en: 'Style & Accessories', tag: 'STYLE_ACCESSORIES', icon: '👘' },
        { id: 'D', en: 'Tools & Everyday Objects', tag: 'TOOLS_EVERYDAY', icon: '🛠️' },
        { id: 'E', en: 'Surprise me', tag: 'SURPRISE_ME', icon: '🌲' }
      ]
    }
  ];

  // --- 8タイプ定義（key = Q1bit+Q2bit+Q3bit） ---------------------------
  var TYPES = {
    '000': { code: 'ea', name: 'The Everyday Aesthete',
      blurb: 'You bring beauty into ordinary moments — nothing kept for "best," everything made to be lived with.',
      cats: ['Tableware', 'Bags', 'Stationery', 'Kitchen'] },
    '001': { code: 'rs', name: 'The Ritual Keeper',
      blurb: 'A cup of tea, a quiet pour — you turn daily habits into small ceremonies rich with story.',
      cats: ['Tea ware', 'Sake ware', 'Lacquer', 'Incense'] },
    '010': { code: 'mc', name: 'The Tastemaker',
      blurb: 'You choose pieces that give a space its character — form, color and texture, composed with intent.',
      cats: ['Kiriko glass', 'Vases', 'Objets', 'Textiles'] },
    '011': { code: 'hk', name: 'The Heritage Keeper',
      blurb: 'You want to hold the craft itself — the region, the technique, the hands — and keep it for years.',
      cats: ['Limited pieces', 'Artist works', 'Rare techniques'] },
    '100': { code: 'tg', name: 'The Thoughtful Giver',
      blurb: 'You give things people actually use — effortless, well-judged, and unmistakably you.',
      cats: ['Cups', 'Plates', 'Towels', 'Pouches'] },
    '101': { code: 'mg', name: 'The Meaning Maker',
      blurb: 'For you a gift carries meaning — the technique, the symbolism, the words that come with it.',
      cats: ['Mizuhiki', 'Kintsugi', 'Lacquer', 'Kumiko'] },
    '110': { code: 'sg', name: 'The Moment Maker',
      blurb: 'You give the piece that stops the room — bold, rare, unforgettable the moment it’s unwrapped.',
      cats: ['Bold Kiriko', 'Decorative', 'Accessories', 'Interior'] },
    '111': { code: 'hg', name: 'The Heirloom Giver',
      blurb: 'You give for the long story — a piece to be named, boxed, and passed on for generations.',
      cats: ['Premium', 'Limited', 'Engraved', 'Boxed'] }
  };

  // ラインナップ表示順（key順に並べると絞り込みが連続領域になり視覚的に自然）
  var TYPE_ORDER = ['000', '001', '010', '011', '100', '101', '110', '111'];

  // config/craft-finder-routing.jsonの公開用mirror。テストで完全一致を保証する。
  var RESULT_ROUTES = {
    "EA": ["handcrafted-ceramic-matcha-tea-bowl", "japanese-lacquer-long-chopsticks", "japanese-lacquer-ballpoint-pen-gold-and-silver-powder"],
    "RS": ["mino-ware-matcha-bowl-jet-black-lacquer-gold-leaf", "kintsugi-design-cafe-au-lait-bowl", "handcrafted-ceramic-matcha-tea-bowl"],
    "MC": ["majoblue-fireworks-flat-plate", "majossian-blue-susuki-flat-plate", "wine-glass-perla-2p-nashiji-jet-black-gold-leaf"],
    "HK": ["kintsugi-design-cafe-au-lait-bowl", "mino-ware-matcha-bowl-jet-black-lacquer-gold-leaf", "majossian-blue-susuki-flat-plate"],
    "TG": ["wine-glass-perla-2p-nashiji-jet-black-gold-leaf", "handcrafted-ceramic-matcha-tea-bowl", "japanese-lacquer-ballpoint-pen-gold-and-silver-powder"],
    "MG": ["kintsugi-design-cafe-au-lait-bowl", "japanese-lacquer-ballpoint-pen-gold-and-silver-powder", "handcrafted-ceramic-matcha-tea-bowl"],
    "SG": ["majoblue-fireworks-flat-plate", "jet-black-gold-dragon-flat-plate", "wine-glass-perla-2p-nashiji-jet-black-gold-leaf"],
    "HG": ["handcrafted-ceramic-matcha-tea-bowl", "kintsugi-design-cafe-au-lait-bowl", "japanese-lacquer-ballpoint-pen-gold-and-silver-powder"]
  };
  var INTEREST_ROUTES = {
    "TABLE_TEA": {"mode":"products","handles":["mino-ware-matcha-bowl-jet-black-lacquer-gold-leaf","handcrafted-ceramic-matcha-tea-bowl","majoblue-fireworks-flat-plate","jet-black-gold-dragon-flat-plate","majossian-blue-susuki-flat-plate","wine-glass-perla-2p-nashiji-jet-black-gold-leaf","japanese-lacquer-long-chopsticks","kintsugi-design-cafe-au-lait-bowl"]},
    "HOME_ATMOSPHERE": {"mode":"products","handles":["majoblue-fireworks-flat-plate","jet-black-gold-dragon-flat-plate","majossian-blue-susuki-flat-plate"]},
    "STYLE_ACCESSORIES": {"mode":"learn_only","url":"/blogs/crafts"},
    "TOOLS_EVERYDAY": {"mode":"products","handles":["japanese-lacquer-ballpoint-pen-gold-and-silver-powder","japanese-lacquer-long-chopsticks"]},
    "SURPRISE_ME": {"mode":"products","handles":["mino-ware-matcha-bowl-jet-black-lacquer-gold-leaf","handcrafted-ceramic-matcha-tea-bowl","japanese-lacquer-ballpoint-pen-gold-and-silver-powder","majoblue-fireworks-flat-plate","jet-black-gold-dragon-flat-plate","majossian-blue-susuki-flat-plate","wine-glass-perla-2p-nashiji-jet-black-gold-leaf","japanese-lacquer-long-chopsticks","kintsugi-design-cafe-au-lait-bowl"]}
  };

  var TAG_LABELS = {
    TABLE_TEA: 'Table & Tea', HOME_ATMOSPHERE: 'Home & Atmosphere',
    STYLE_ACCESSORIES: 'Style & Accessories', TOOLS_EVERYDAY: 'Tools & Everyday Objects',
    SURPRISE_ME: 'Surprise me'
  };

  // ------------------------------------------------------------------ utils
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ------------------------------------------------------------------ instance
  function CraftFinder(root) {
    this.root = root;
    var cfgEl = root.querySelector('[data-cf-config]');
    this.cfg = {};
    if (cfgEl) { try { this.cfg = JSON.parse(cfgEl.textContent) || {}; } catch (e) {} }
    this.assets = this.cfg.assets || {};
    this.products = this.cfg.products || [];
    this.shopUrl = this.cfg.shopUrl || '/collections/all';
    this.source = this.cfg.source || 'finder';
    this.privacyUrl = this.cfg.privacyUrl || '/policies/privacy-policy';
    this.shopName = this.cfg.shopName || '';
    this.klaviyo = this.cfg.klaviyo || {};

    this.state = { answers: {}, emailed: false };
    this.stage = root.querySelector('[data-cf-stage]');
    this.screens = {};

    this.restore();
    this.readUrl();
    this.build();
    this.resume();
  }

  // 状態: 保存・復元 -------------------------------------------------------
  CraftFinder.prototype.save = function () {
    // email・氏名はKlaviyo送信中のメモリだけで扱い、ブラウザへ永続保存しない。
    var persisted = { answers: this.state.answers || {}, emailed: !!this.state.emailed };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted)); } catch (e) {}
  };
  CraftFinder.prototype.restore = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (s && s.answers) {
          this.state = { answers: s.answers, emailed: !!s.emailed };
          // 旧版が保存したemail・firstNameを同じキーから除去する。
          this.save();
        }
      }
    } catch (e) {}
  };
  CraftFinder.prototype.readUrl = function () {
    var p = new URLSearchParams(window.location.search);
    if (p.get('src')) this.source = p.get('src');
    // ポップアップ/記事シードからQ1等を引き継いだhand-offは「新規診断の開始」とみなす。
    // 過去に完走したlocalStorageの回答(q2/q3等)が残っていると、Q1を上書きしても
    // prefixが3問揃った扱いになり、途中を飛ばして結果へ直行してしまう。
    // hand-off時はURLで指定された回答のみを残すよう回答をリセットする(email情報は維持)。
    var handoff = ['q1', 'q2', 'q3', 'q4'].some(function (qid) { return p.get(qid) != null; });
    if (handoff) this.state.answers = {};
    ['q1', 'q2', 'q3'].forEach(function (qid) {
      var v = p.get(qid);
      if (v == null) return;
      var q = QUESTIONS.filter(function (x) { return x.id === qid; })[0];
      var a = null;
      if (v === '0' || v === '1') { // bit指定: 該当bitの先頭回答を採用
        a = q.answers.filter(function (x) { return x.bit === v; })[0];
      } else { // 回答ID指定(A-D)
        a = q.answers.filter(function (x) { return x.id === v.toUpperCase(); })[0];
      }
      if (a) this.state.answers[qid] = a.id;
    }, this);
    var q4 = p.get('q4');
    if (q4) {
      var a4 = QUESTIONS[3].answers.filter(function (x) { return x.id === q4.toUpperCase(); })[0];
      if (a4) this.state.answers.q4 = a4.id;
    }
  };

  // 判定 -----------------------------------------------------------------
  CraftFinder.prototype.answerObj = function (qid) {
    var q = QUESTIONS.filter(function (x) { return x.id === qid; })[0];
    var aid = this.state.answers[qid];
    if (!aid) return null;
    return q.answers.filter(function (x) { return x.id === aid; })[0] || null;
  };
  CraftFinder.prototype.prefix = function () {
    var s = '';
    for (var i = 0; i < 3; i++) {
      var a = this.answerObj('q' + (i + 1));
      if (!a) break;
      s += a.bit;
    }
    return s;
  };
  CraftFinder.prototype.typeKey = function () {
    var pre = this.prefix();
    return pre.length === 3 ? pre : null;
  };

  // GA4 -----------------------------------------------------------------
  CraftFinder.prototype.baseParams = function () {
    var key = this.typeKey();
    var a4 = this.answerObj('q4');
    return {
      source_surface: this.source,
      language: 'en',
      type_key: key || undefined,
      craft_type: key ? TYPES[key].code.toUpperCase() : undefined,
      interest_category: a4 ? a4.tag : undefined
    };
  };
  CraftFinder.prototype.track = function (name, extra) {
    var data = this.baseParams();
    if (extra) for (var k in extra) data[k] = extra[k];
    data.event = name;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    if (typeof window.gtag === 'function') {
      var p = {}; for (var k2 in data) if (k2 !== 'event') p[k2] = data[k2];
      window.gtag('event', name, p);
    }
  };

  // 構築 -----------------------------------------------------------------
  CraftFinder.prototype.build = function () {
    this.stage.innerHTML = '';
    this.buildIntro();
    QUESTIONS.forEach(function (q) { this.buildQuestion(q); }, this);
    this.buildEmail();
    this.buildResult();
  };

  CraftFinder.prototype.showScreen = function (name) {
    Object.keys(this.screens).forEach(function (k) {
      this.screens[k].classList.toggle('is-active', k === name);
    }, this);
    this.current = name;
    // aria-live 更新のためフォーカスをステージ先頭へ
    var active = this.screens[name];
    if (active) {
      var focusable = active.querySelector('button, input, a');
      if (focusable && name !== 'intro') { try { focusable.focus({ preventScroll: true }); } catch (e) {} }
    }
  };

  CraftFinder.prototype.addScreen = function (name, node) {
    node.className = 'cf-screen';
    node.setAttribute('data-cf-screen', name);
    this.screens[name] = node;
    this.stage.appendChild(node);
  };

  // intro ----------------------------------------------------------------
  CraftFinder.prototype.buildIntro = function () {
    var s = el('div', 'cf-screen cf-intro');
    var eyebrow = this.cfg.eyebrow || 'Japanese Craft Finder';
    var title = this.cfg.heading || 'Which Japanese craft spirit are you?';
    var lead = this.cfg.lead || 'Answer 4 short questions and meet the craft spirit that matches how you live, give and treasure beautiful things.';
    s.appendChild(el('p', 'cf-eyebrow', esc(eyebrow)));
    s.appendChild(el('h2', 'cf-title', esc(title)));
    s.appendChild(el('p', 'cf-lead', esc(lead)));

    var grid = el('div', 'cf-intro__spirits');
    TYPE_ORDER.forEach(function (key) {
      var t = TYPES[key];
      var cell = el('div', 'cf-intro__spirit');
      var img = new Image();
      img.src = this.assetUrl(t.code, 'sm');
      img.alt = t.name;
      img.loading = 'lazy';
      cell.appendChild(img);
      grid.appendChild(cell);
    }, this);
    s.appendChild(grid);

    var btn = el('button', 'cf-btn', 'Find my craft spirit');
    btn.type = 'button';
    var self = this;
    btn.addEventListener('click', function () {
      self.track('cta_click', { cta_position: 'finder_intro' });
      self.track('form_start');
      self.goToFirstUnanswered();
    });
    s.appendChild(btn);

    var meta = el('div', 'cf-meta');
    meta.appendChild(el('span', null, '⏱️ 30 seconds'));
    meta.appendChild(el('span', null, '📝 4 questions'));
    meta.appendChild(el('span', null, '🌀 8 craft spirits'));
    s.appendChild(meta);

    this.screens.intro = s;
    this.stage.appendChild(s);
  };

  // question -------------------------------------------------------------
  CraftFinder.prototype.buildQuestion = function (q) {
    var self = this;
    var idx = QUESTIONS.indexOf(q);
    var s = el('div', 'cf-screen cf-q');

    // head: progress + lineup
    var head = el('div', 'cf-quizhead');
    var prog = el('div', 'cf-progress');
    var track = el('div', 'cf-progress__track');
    var bar = el('div', 'cf-progress__bar');
    track.appendChild(bar);
    var label = el('span', 'cf-progress__label', 'Q' + (idx + 1) + ' / ' + QUESTIONS.length);
    prog.appendChild(label);
    prog.appendChild(track);
    head.appendChild(prog);
    s._bar = bar;
    s._label = label;

    // spirit lineup（全問を通して8体すべてを常時ハイライト表示する）
    var lineup = el('div', 'cf-lineup');
    var row = el('div', 'cf-lineup__row');
    s._spirits = {};
    TYPE_ORDER.forEach(function (key) {
      var t = TYPES[key];
      var sp = el('div', 'cf-spirit');
      sp.setAttribute('data-key', key);
      var img = new Image();
      img.src = self.assetUrl(t.code, 'sm');
      img.alt = t.name;
      img.loading = 'lazy';
      sp.appendChild(img);
      row.appendChild(sp);
      s._spirits[key] = sp;
    });
    lineup.appendChild(row);
    var caption = el('p', 'cf-lineup__caption');
    lineup.appendChild(caption);
    s._caption = caption;
    head.appendChild(lineup);
    s.appendChild(head);

    // question text
    s.appendChild(el('h2', 'cf-q__text', esc(q.en)));
    if (q.helper) s.appendChild(el('p', 'cf-q__helper', esc(q.helper)));

    // options
    var opts = el('div', 'cf-options');
    opts.setAttribute('role', 'group');
    q.answers.forEach(function (a) {
      var b = el('button', 'cf-option');
      b.type = 'button';
      b.setAttribute('data-answer', a.id);
      b.innerHTML =
        '<span class="cf-option__icon" aria-hidden="true">' + a.icon + '</span>' +
        '<span class="cf-option__body">' +
        '<span class="cf-option__en">' + esc(a.en) + '</span>' +
        '</span>';
      b.addEventListener('click', function () { self.selectAnswer(q, a); });
      opts.appendChild(b);
    });
    s.appendChild(opts);

    // nav
    var nav = el('div', 'cf-q__nav');
    var back = el('button', 'cf-textlink', '← Back');
    back.type = 'button';
    back.addEventListener('click', function () { self.goBack(idx); });
    nav.appendChild(back);
    s.appendChild(nav);

    this.addScreen(q.id, s);
  };

  CraftFinder.prototype.renderQuestionState = function (q) {
    var s = this.screens[q.id];
    var idx = QUESTIONS.indexOf(q);
    // progress: 質問に到達した時点でバーを進める（未回答でも現在の設問を半分カウント）。
    // これで最初の設問でも0%にならず、最後の設問では「もう少しで終わる」感が出る。
    var answered = !!this.state.answers[q.id];
    var pct = ((idx + (answered ? 1 : 0.5)) / QUESTIONS.length) * 100;
    s._bar.style.width = pct + '%';
    // 残り1問になったら「あと少し」であることを明示してドロップオフを抑える。
    var remaining = QUESTIONS.length - (idx + 1);
    if (remaining === 0) s._label.textContent = 'Last question · almost there!';
    else s._label.textContent = 'Q' + (idx + 1) + ' / ' + QUESTIONS.length;
    // selected highlight
    var sel = this.state.answers[q.id];
    Array.prototype.forEach.call(s.querySelectorAll('.cf-option'), function (b) {
      b.classList.toggle('is-selected', b.getAttribute('data-answer') === sel);
    });
    // 回答途中でタイプが絞られると「自分のタイプを知りたい」という登録動機が弱まるため、
    // 全8体を常に最初のハイライト状態のまま維持し、どのタイプに向かっているかを見せない。
    TYPE_ORDER.forEach(function (key) {
      var sp = s._spirits[key];
      sp.classList.remove('is-out');
      sp.classList.remove('is-chosen');
    });
    s._caption.innerHTML = '<b>8</b> craft spirits — one of them is you';
  };

  CraftFinder.prototype.selectAnswer = function (q, a) {
    this.state.answers[q.id] = a.id;
    this.save();
    var idx = QUESTIONS.indexOf(q);
    this.renderQuestionState(q);
    this.track('question_answered', { question_id: q.id, answer_id: a.id });
    if (q.id === 'q4') this.track('lead_preference_selected');

    var self = this;
    // 選択の余韻を見せてから次へ
    var delay = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 60 : 520;
    setTimeout(function () {
      if (idx < QUESTIONS.length - 1) {
        var next = QUESTIONS[idx + 1];
        self.showScreen(next.id);
        self.renderQuestionState(next);
      } else {
        // Q4完了 → メールゲート（未登録なら）or 結果
        if (self.state.emailed) self.openResult();
        else { self.renderEmail(); self.showScreen('email'); }
      }
    }, delay);
  };

  CraftFinder.prototype.goBack = function (idx) {
    if (idx === 0) { this.showScreen('intro'); return; }
    var prev = QUESTIONS[idx - 1];
    this.showScreen(prev.id);
    this.renderQuestionState(prev);
  };

  // email gate -----------------------------------------------------------
  CraftFinder.prototype.buildEmail = function () {
    var self = this;
    var s = el('div', 'cf-screen cf-email');
    s.appendChild(el('p', 'cf-eyebrow', 'Your result is ready'));
    s.appendChild(el('h2', 'cf-title', 'Which craft spirit are you?'));
    // タイプはメール登録後のサプライズにするため、ここでは実際のキャラ画像ではなく
    // 「？」のミステリー状態を表示し、登録の動機（自分のタイプを知りたい）を高める。
    var preview = el('div', 'cf-email__preview cf-email__preview--mystery');
    preview.setAttribute('aria-hidden', 'true');
    preview.appendChild(el('span', 'cf-email__qmark', '?'));
    s.appendChild(preview);
    s.appendChild(el('p', 'cf-lead', 'You’ve been matched with one of the 8 craft spirits — but which one? Enter your email to reveal your type and see pieces chosen for you. No spam — just craft.'));

    var card = el('div', 'cf-email__card');

    var nameField = el('div', 'cf-field');
    var nameLabel = el('label', null, 'First name (optional)');
    nameLabel.setAttribute('for', 'cf-name-input');
    var nameInput = el('input', 'cf-input');
    nameInput.type = 'text';
    nameInput.id = 'cf-name-input';
    nameInput.placeholder = 'Your name';
    nameInput.autocomplete = 'given-name';
    nameField.appendChild(nameLabel);
    nameField.appendChild(nameInput);
    card.appendChild(nameField);

    var field = el('div', 'cf-field');
    var label = el('label', null, 'Email address');
    label.setAttribute('for', 'cf-email-input');
    var input = el('input', 'cf-input');
    input.type = 'email';
    input.id = 'cf-email-input';
    input.placeholder = 'you@example.com';
    input.autocomplete = 'email';
    field.appendChild(label);
    field.appendChild(input);
    card.appendChild(field);

    var actions = el('div', 'cf-email__actions');
    var submit = el('button', 'cf-btn', 'Send me my result');
    submit.type = 'button';
    var submitLabel = submit.textContent;
    var status = el('p', 'cf-email__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    submit.addEventListener('click', function () {
      if (submit.disabled) return;
      var val = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        input.focus();
        input.setCustomValidity('Please enter a valid email');
        input.reportValidity();
        return;
      }
      input.setCustomValidity('');
      self.state.email = val;
      self.state.firstName = (nameInput.value || '').trim();
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'Sending…';
      status.textContent = 'Saving your result…';
      self.track('email_consent_granted', { lead_magnet: 'craft_finder', craft_finder_source: self.source });
      // 購読と完了イベントの両方が成功した後だけ、登録済みとして保存する。
      self.sendToKlaviyo(val).then(function () {
        self.state.emailed = true;
        self.save();
        self.track('form_submit', { lead_magnet: 'craft_finder' });
        self.track('generate_lead', { lead_magnet: 'craft_finder' });
        self.track('email_signup_succeeded', { lead_magnet: 'craft_finder', craft_finder_source: self.source });
        self.openResult();
      }).catch(function () {
        status.textContent = 'We could not save your result. Please try again.';
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        submit.textContent = submitLabel;
        input.focus();
      });
    });
    actions.appendChild(submit);
    card.appendChild(actions);
    card.appendChild(status);
    // マーケティングメール+プライバシーポリシーへの同意注意書き（チェックボックスは置かず文言のみ）
    var esc = function (s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    };
    var brand = self.shopName ? (' from ' + esc(self.shopName)) : '';
    var note = el('p', 'cf-email__note');
    note.innerHTML = 'By entering your email you agree to our '
      + '<a href="' + esc(self.privacyUrl) + '" target="_blank" rel="noopener">Privacy Policy</a>'
      + ' and consent to receive marketing emails' + brand
      + '. You can unsubscribe in one click at any time.';
    card.appendChild(note);
    s.appendChild(card);

    this.addScreen('email', s);
  };

  CraftFinder.prototype.renderEmail = function () {
    // メールゲートではタイプを明かさない（「？」表示のまま）。登録後に結果画面で初めて公開する。
  };

  // result ---------------------------------------------------------------
  CraftFinder.prototype.buildResult = function () {
    var s = el('div', 'cf-screen cf-result');
    s._body = el('div');
    s.appendChild(s._body);
    this.addScreen('result', s);
  };

  CraftFinder.prototype.openResult = function () {
    this.renderResult();
    this.showScreen('result');
    // GA4 ecommerceのview_itemは実商品ページだけに限定する。
    // 診断結果をview_itemへ混ぜると商品閲覧ファネルが過大集計される。
    this.track('craft_finder_result_view', { item_category: 'craft_finder_result' });
    try { this.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
  };

  CraftFinder.prototype.renderResult = function () {
    var key = this.typeKey();
    if (!key) { this.goToFirstUnanswered(); return; }
    var t = TYPES[key];
    var a4 = this.answerObj('q4');
    var tag = a4 ? a4.tag : null;
    var self = this;
    var body = this.screens.result._body;
    body.innerHTML = '';

    var hero = el('div', 'cf-result__hero');
    var char = el('div', 'cf-result__char');
    var img = new Image();
    img.src = this.assetUrl(t.code, 'full');
    img.alt = t.name;
    char.appendChild(img);
    hero.appendChild(char);
    hero.appendChild(el('p', 'cf-result__code', 'Your Japanese Craft Spirit'));
    hero.appendChild(el('h2', 'cf-result__name', esc(t.name)));
    hero.appendChild(el('p', 'cf-result__blurb', esc(t.blurb)));
    body.appendChild(hero);

    // recommended categories
    var catBlock = el('div', 'cf-block');
    catBlock.appendChild(el('p', 'cf-block__title', 'You’re drawn to'));
    var chips = el('div', 'cf-chips');
    t.cats.forEach(function (c) { chips.appendChild(el('span', 'cf-chip', esc(c))); });
    catBlock.appendChild(chips);
    body.appendChild(catBlock);

    // interest starting point
    if (tag) {
      var ib = el('div', 'cf-block');
      ib.appendChild(el('p', 'cf-block__title', 'Your starting point'));
      ib.appendChild(el('div', 'cf-chips', '<span class="cf-chip">' + esc(TAG_LABELS[tag] || tag) + '</span>'));
      body.appendChild(ib);
    }

    // typeを優先し、interestとの積集合→interest候補→type候補の順で在庫商品を選ぶ。
    var routedProducts = this.routedProducts(t.code.toUpperCase(), tag);
    if (routedProducts.length) {
      var pb = el('div', 'cf-block');
      pb.appendChild(el('p', 'cf-block__title', 'Pieces to begin with'));
      var grid = el('div', 'cf-products');
      routedProducts.forEach(function (p) {
        var a = el('a', 'cf-prod');
        a.href = p.url;
        a.innerHTML =
          '<span class="cf-prod__img">' + (p.image ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') + '</span>' +
          '<span class="cf-prod__title">' + esc(p.title) + '</span>' +
          '<span class="cf-prod__price">' + esc(p.price || '') + '</span>';
        a.addEventListener('click', function () {
          self.track('craft_finder_product_click', {
            item_id: p.handle || p.url,
            craft_type: t.code.toUpperCase(),
            interest_category: tag || 'UNSPECIFIED',
            craft_finder_source: self.source
          });
        });
        grid.appendChild(a);
      });
      pb.appendChild(grid);
      body.appendChild(pb);
    }

    // 対応商品0のカテゴリは存在しない商品を推薦せず、Learn-onlyへ送る。
    if (tag && INTEREST_ROUTES[tag] && INTEREST_ROUTES[tag].mode === 'learn_only') {
      var cs = el('div', 'cf-comingsoon');
      cs.innerHTML = '<b>' + esc(TAG_LABELS[tag] || tag) + ' — coming soon.</b> ' +
        'We’re curating this category now. <a href="' + esc(INTEREST_ROUTES[tag].url) + '">Explore the craft journal</a> while we build the collection.';
      body.appendChild(cs);
    }

    // CTA
    var cta = el('div', 'cf-result__cta');
    var shop = el('a', 'cf-btn');
    shop.href = this.shopUrl;
    shop.textContent = 'Explore the collection';
    shop.addEventListener('click', function () {
      self.track('cta_click', {
        cta_position: 'finder_result',
        craft_type: t.code.toUpperCase(),
        interest_category: tag || 'UNSPECIFIED',
        craft_finder_source: self.source
      });
    });
    var retake = el('button', 'cf-btn cf-btn--ghost', 'Retake');
    retake.type = 'button';
    retake.addEventListener('click', function () { self.reset(); });
    cta.appendChild(shop);
    cta.appendChild(retake);
    body.appendChild(cta);
  };

  CraftFinder.prototype.routedProducts = function (typeCode, interestTag) {
    var byHandle = {};
    this.products.forEach(function (product) {
      if (product.handle && product.available !== false) byHandle[product.handle] = product;
    });
    var interest = INTEREST_ROUTES[interestTag] || { mode: 'products', handles: [] };
    if (interest.mode === 'learn_only') return [];
    var typeHandles = RESULT_ROUTES[typeCode] || [];
    var interestHandles = interest.handles || [];
    var interestSet = {};
    interestHandles.forEach(function (handle) { interestSet[handle] = true; });
    var ordered = typeHandles.filter(function (handle) { return interestSet[handle]; })
      .concat(interestHandles)
      .concat(typeHandles);
    var seen = {};
    var result = [];
    ordered.forEach(function (handle) {
      if (!seen[handle] && byHandle[handle] && result.length < 3) {
        seen[handle] = true;
        result.push(byHandle[handle]);
      }
    });
    return result;
  };

  // flow helpers ---------------------------------------------------------
  CraftFinder.prototype.goToFirstUnanswered = function () {
    for (var i = 0; i < QUESTIONS.length; i++) {
      if (!this.state.answers[QUESTIONS[i].id]) {
        this.showScreen(QUESTIONS[i].id);
        this.renderQuestionState(QUESTIONS[i]);
        return;
      }
    }
    // 全問回答済み
    if (this.state.emailed) this.openResult();
    else { this.renderEmail(); this.showScreen('email'); }
  };

  CraftFinder.prototype.resume = function () {
    var answered = this.prefix().length;
    var hasQ4 = !!this.state.answers.q4;
    if (answered === 0 && !hasQ4) {
      this.showScreen('intro');
      this.track('cta_impression', { cta_position: 'finder_intro' });
    } else {
      // 記事シード等で一部回答済み → 続きから
      this.goToFirstUnanswered();
    }
  };

  CraftFinder.prototype.reset = function () {
    this.state = { answers: {}, emailed: false };
    this.save();
    this.build();
    this.showScreen('intro');
    this.track('cta_impression', { cta_position: 'finder_intro' });
  };

  // Klaviyo --------------------------------------------------------------
  // 診断結果をKlaviyoへ渡すプロパティ束（プロフィール属性 兼 イベント属性）
  CraftFinder.prototype.klaviyoProps = function () {
    var key = this.typeKey();
    var t = key ? TYPES[key] : null;
    var a4 = this.answerObj('q4');
    return {
      craft_type: t ? t.code.toUpperCase() : null,
      craft_type_key: key || null,
      craft_type_name: t ? t.name : null,
      interest_category: a4 ? a4.tag : null,
      craft_finder_source: this.source,
      quiz_q1: this.state.answers.q1 || null,
      quiz_q2: this.state.answers.q2 || null,
      quiz_q3: this.state.answers.q3 || null,
      quiz_q4: this.state.answers.q4 || null
    };
  };

  // メール登録時にKlaviyoへ送信する。公開APIキー(companyId)のみ使用し、
  // (1)リスト購読+カスタムプロパティ保存 (2)フロー起点イベント発火 を行う。
  // クライアント公開APIのため秘密鍵は不要。両リクエスト成功時だけresolveする。
  CraftFinder.prototype.sendToKlaviyo = function (email) {
    var kl = this.klaviyo || {};
    if (!kl.companyId || !kl.listId || !email) return Promise.reject(new Error('Klaviyo configuration is incomplete'));
    var headers = { 'Content-Type': 'application/json', 'revision': '2024-10-15' };
    var props = this.klaviyoProps();
    // 任意入力の名前があればプロフィール属性に含める（未入力なら送らない）
    var firstName = (this.state && this.state.firstName) || '';

    // (1) リスト購読 + プロフィールへカスタムプロパティ保存（同意記録はリスト設定に従う）
    var subAttrs = firstName
      ? { email: email, first_name: firstName, properties: props }
      : { email: email, properties: props };
    var subBody = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Craft Finder',
          profile: {
            data: { type: 'profile', attributes: subAttrs }
          }
        },
        relationships: { list: { data: { type: 'list', id: kl.listId } } }
      }
    };

    // (2) フロー起点イベント "Craft Finder Completed" を発火
    var evBody = {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: 'Craft Finder Completed' } } },
          properties: props,
          profile: { data: { type: 'profile', attributes: firstName ? { email: email, first_name: firstName } : { email: email } } }
        }
      }
    };
    function post(url, body) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, 10000) : null;
      return fetch(url, { method: 'POST', headers: headers, body: JSON.stringify(body), keepalive: true, signal: controller ? controller.signal : undefined })
        .then(function (response) {
          if (timer) clearTimeout(timer);
          if (!response.ok) throw new Error('Klaviyo request failed: ' + response.status);
          return response;
        }, function (error) {
          if (timer) clearTimeout(timer);
          throw error;
        });
    }

    var companyQuery = '?company_id=' + encodeURIComponent(kl.companyId);
    return post('https://a.klaviyo.com/client/subscriptions/' + companyQuery, subBody)
      .then(function () { return post('https://a.klaviyo.com/client/events/' + companyQuery, evBody); });
  };

  // assets ---------------------------------------------------------------
  CraftFinder.prototype.assetUrl = function (code, size) {
    var a = this.assets[code];
    if (a) return size === 'sm' ? (a.sm || a.full) : (a.full || a.sm);
    return '';
  };

  // ------------------------------------------------------------------ init
  function init() {
    var roots = document.querySelectorAll('.craft-finder[data-cf-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root._cfInited) return;
      root._cfInited = true;
      new CraftFinder(root);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.CraftFinder = CraftFinder;
})();
